import {
	personIconMap,
	personPillMap,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/RightPanel/utils';
import { EditField } from '@dealstack/frontend/shared/components/Documents/FormFields/EditField';
import {
	DocumentDetailsRes,
	DocumentFieldRes,
	DocumentFields,
	DocumentFormAction,
	DocumentPersonRelationRes,
} from '@dealstack/shared/lib/generated';
import { Flex, Loader } from '@mantine/core';
import { InlineFieldSubmission } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/InlineFieldSubmission';
import { ViewField } from '@dealstack/frontend/shared/components/Documents/FormFields/ViewField/ViewField';
import { FormField } from '@dealstack/frontend/shared/components/Documents/FormFields/types';
import { Control, UseFormReturn } from 'react-hook-form';
import { FieldWrapper } from '@dealstack/frontend/shared/components/Documents/FormFields/styled';
import { PersonsListView } from '@dealstack/frontend/shared/components/Documents/FormFields/DocumentFieldView/PersonsListView';
import { mapValueToField } from '@dealstack/frontend/shared/components/Documents/FormFields/DocumentFieldView/DocumentFieldView.utils';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	DocumentFieldFilter,
	DocumentLinkTarget,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';

interface Props {
	editFieldKey: string;
	fieldAction?: DocumentFormAction | null;
	onCancel: () => void;
	onSave: (field: FormField) => Promise<void>;
	documentId: string;
	dealId: string;
	relatedPeople: DocumentPersonRelationRes[];
	documentDetails: DocumentDetailsRes | undefined;
	isLoading: boolean;
	control: Control<DocumentDetailsRes>;
	handleEditField: (fieldKey: DocumentFields, action?: DocumentFormAction) => void;
	formState: UseFormReturn<DocumentDetailsRes>;
	fields?: DocumentFieldRes[];
	target?: DocumentLinkTarget;
	dataOperations?: TableOperations<DocumentFieldFilter>;
}

export const DocumentFieldView = ({
	editFieldKey,
	fieldAction,
	onCancel,
	onSave,
	documentId,
	dealId,
	relatedPeople,
	documentDetails,
	isLoading,
	control,
	formState,
	handleEditField,
	fields,
	target,
	dataOperations,
}: Props) => {
	const { value: isDealDocumentsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTOR_PORTAL_DEAL_DOCUMENTS,
		{ dealId }
	);

	// DC-494: left undefined when the flag is off so PersonsListView renders no toggles at all.
	const investorVisibilityByPersonId = isDealDocumentsEnabled
		? Object.fromEntries(
				relatedPeople
					.filter((person) => person.visibleToInvestor !== undefined)
					.map((person) => [person.personId, person.visibleToInvestor === true])
			)
		: undefined;

	if (
		editFieldKey === DocumentFields.RELATED_PEOPLE &&
		fieldAction &&
		fieldAction === DocumentFormAction.VIEW_ALL_RELATED_PEOPLE
	) {
		return (
			<PersonsListView
				variant="primary"
				documentId={documentId}
				dealId={dealId}
				onCancel={onCancel}
				items={relatedPeople.map((person) => ({
					id: person.personId,
					value: person.personName,
					icon: personIconMap(person.personType),
					badge: personPillMap(person.documentPersonType) ?? undefined,
				}))}
				target={target}
				dataOperations={dataOperations}
				investorVisibilityByPersonId={investorVisibilityByPersonId}
			/>
		);
	}

	return (
		<Flex direction="column" gap="1rem" w="100%">
			{fields?.map((field) => (
				<FieldWrapper key={`${field.fieldKey}_${field.label}`}>
					<DocumentFieldRender
						formState={formState}
						dealId={dealId}
						documentId={documentId}
						field={field}
						relatedPeople={relatedPeople}
						documentDetails={documentDetails}
						isLoading={isLoading}
						editFieldKey={editFieldKey}
						fieldAction={fieldAction}
						onCancel={onCancel}
						onSave={onSave}
						handleEditField={handleEditField}
						control={control}
						target={target}
						dataOperations={dataOperations}
					/>
				</FieldWrapper>
			))}
		</Flex>
	);
};

const DocumentFieldRender = ({
	field,
	editFieldKey,
	onCancel,
	onSave,
	dealId,
	isLoading,
	control,
	relatedPeople,
	documentDetails,
	documentId,
	handleEditField,
	target,
	dataOperations,
}: Props & { field: DocumentFieldRes }) => {
	const { value: isDealDocumentsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTOR_PORTAL_DEAL_DOCUMENTS,
		{ dealId }
	);

	// DC-494: left undefined when the flag is off so PersonsListView renders no toggles at all.
	const investorVisibilityByPersonId = isDealDocumentsEnabled
		? Object.fromEntries(
				relatedPeople
					.filter((person) => person.visibleToInvestor !== undefined)
					.map((person) => [person.personId, person.visibleToInvestor === true])
			)
		: undefined;

	if (
		editFieldKey === DocumentFields.RELATED_PEOPLE &&
		field.fieldKey === DocumentFields.RELATED_PEOPLE
	) {
		return (
			<PersonsListView
				variant="secondary"
				documentId={documentId}
				dealId={dealId}
				onCancel={onCancel}
				items={relatedPeople.map((person) => ({
					id: person.personId,
					value: person.personName,
					icon: personIconMap(person.personType),
					badge: personPillMap(person.documentPersonType) ?? undefined,
				}))}
				target={target}
				dataOperations={dataOperations}
				investorVisibilityByPersonId={investorVisibilityByPersonId}
			/>
		);
	}

	if (editFieldKey === field.fieldKey && !field.action) {
		return (
			<Flex direction="row" gap="0.5rem" align="flex-end" w="100%">
				<div style={{ flex: 1 }}>
					<EditField field={field} control={control} />
				</div>
				{isLoading ? (
					<Loader />
				) : (
					<InlineFieldSubmission onCancel={onCancel} onSave={onSave} field={field} />
				)}
			</Flex>
		);
	}

	return (
		<ViewField
			setEditFieldKey={handleEditField}
			field={{
				...field,
				value: mapValueToField({
					fieldKey: field.fieldKey as keyof DocumentDetailsRes,
					relatedPeople,
					documentDetails,
					dealId,
				}),
			}}
		/>
	);
};
